import React, { useCallback, useEffect, useState } from 'react';
import { GridContainer, Grid } from 'src/shared/grid';
import { Formik, Form } from 'formik';
import { alpha, FormControlLabel, styled, Switch, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import 'src/features/field-sample//FieldSample.scss';
import * as Yup from 'yup';
import { useAddAlert } from 'src/store/actions';
import { useStoreContext } from 'src/store/context';
import { errorResponse, regex2decimals, validateInt, ValidCharacters } from 'src/store/common';
import LoadingSpinner from 'src/shared/loading/loading';
import { getStationMeta } from 'src/apis/getWeatherCalls';
import { alertMessages } from 'src/constants';
import { createUpdateStationMeta } from 'src/apis/mutationsStations';


export const validationSchema = Yup.object().shape(
    {
        avg_annual_precip: Yup.number().nullable().min(0, "Avg Annual Precip must be greater than or equal to 0")
            .max(700, "Avg Annual Precip must be less than or equal to 700")
            .required('Avg Annual Precip is Required'),
        vpd_min_percentile: Yup.number().nullable().min(0, "VPD Min must be greater than or equal to 0")
            .max(100, "VPD Min must be less than or equal to 100")
            .required('VPD Min is Required')
            .test("less-or-equal-max", "VPD Min must be less than or equal to VPD Max", function (value) {
                const max = this.parent.vpd_max_percentile;
                if (value == null || max == null || max < 0) return true;
                return value <= max;
            }),
        vpd_max_percentile: Yup.number().nullable().min(0, "VPD Max must be greater than or equal to 0")
            .max(100, "VPD Max must be less than or equal to 100")
            .required('VPD Max is Required')
            .test("greater-or-equal-max", "VPD Max must be greater than or equal to VPD Min", function (value) {
                const min = this.parent.vpd_min_percentile;
                if (value == null || min == null || min > 10000) return true;
                return value >= min;
            }),
    }
);

const GreenSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'rgb(0, 128, 0)',
    '&:hover': {
      backgroundColor: alpha('rgb(0, 128, 0)', theme.palette.action.hoverOpacity),
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: 'rgb(0, 128, 0)',
  },
}));

type StationProps = {
    setOpen: any,
    setCreateUpdateStationSuccess?: any,
    editStationId?: any,
}
interface FormVals {
    stationId: number | null,
    avg_annual_precip: number | null,
    vpd_min_percentile: number | null,
    vpd_max_percentile: number | null,

    weather_daily_visible: boolean,
    nfdrs_daily_visible: boolean,
    weather_hourly_visible: boolean,
    nfdrs_hourly_visible: boolean,

    weather_daily_download: boolean,
    weather_hourly_download: boolean,
    nfdrs_hourly_download: boolean,
    nfdrs_daily_download: boolean,

    station_metadata_visible: boolean,
    produce_nfdrs: boolean,
    ingest_observation: boolean,

    nfdrs_visibility: boolean,
    weather_visibility: boolean,
}
export const AddStation: React.FC<StationProps> = ({ setOpen, editStationId, setCreateUpdateStationSuccess}) => {

    const [state, dispatch] = useStoreContext();
    const addAlert = useAddAlert(dispatch);
    const [loading, setLoading] = useState<boolean>(false);
    const [hasHistoric, setHasHistoric] = useState<boolean>(true);
    const [stationFailCount, setStationFailCount] = useState<number>(0);

    const [initVals, setInitVals] = useState<FormVals>({
        stationId: editStationId,
        avg_annual_precip: null,
        vpd_min_percentile: 75,
        vpd_max_percentile: 95,

        weather_daily_visible: true,
        nfdrs_daily_visible: true,
        weather_hourly_visible: true,
        nfdrs_hourly_visible: true,

        weather_daily_download: true,
        weather_hourly_download: true,
        nfdrs_hourly_download: true,
        nfdrs_daily_download: true,

        station_metadata_visible: true,
        produce_nfdrs: true,
        ingest_observation: true,

        nfdrs_visibility: true,
        weather_visibility: true,
    });

    const getMeta = useCallback((stationId: any, counter: number) => {
        (async () => {
            try {
                const varRequest = {
                    stationId: stationId.toString()
                };
                const response: any = await getStationMeta(varRequest, addAlert, state?.user);
                if (response) {
                    setStationFailCount(0);
                    setHasHistoric(response.has_historic_data)
                    const stationmeta = {
                        stationId: response.station_id,
                        avg_annual_precip: Number(response.avg_annual_precip),
                        vpd_min_percentile: response.vpd_min_percentile,
                        vpd_max_percentile: response.vpd_max_percentile,

                        weather_daily_visible: response.weather_daily_visible,
                        nfdrs_daily_visible: response.nfdrs_daily_visible,
                        weather_hourly_visible: response.weather_hourly_visible,
                        nfdrs_hourly_visible: response.nfdrs_hourly_visible,

                        weather_daily_download: response.weather_daily_download,
                        weather_hourly_download: response.weather_hourly_download,
                        nfdrs_hourly_download: response.nfdrs_hourly_download,
                        nfdrs_daily_download: response.nfdrs_daily_download,

                        station_metadata_visible: response.station_metadata_visible,
                        produce_nfdrs: response.produce_nfdrs,
                        ingest_observation: response.ingest_observation,

                        nfdrs_visibility: response.nfdrs_daily_visible || response.nfdrs_hourly_visible || response.nfdrs_hourly_download || response.nfdrs_daily_download,
                        weather_visibility: response.weather_daily_visible || response.weather_hourly_visible || response.weather_hourly_download || response.weather_daily_download
                    }
                    setInitVals(stationmeta)

                }
                else{
                    setStationFailCount(counter+1);
                }
            }
            catch (error) {
                console.log('error', error)
                setStationFailCount(counter+1)
            }
        })();
    }, [addAlert, state?.user]);

    
    const updateStationMeta = useCallback((valuesRequested: any) => {
        (async () => {
            try {
                let valuesToSend = valuesRequested;
                if(hasHistoric===false){
                    valuesToSend ={
                        ...valuesRequested,
                        vpd_min_percentile: null,
                        vpd_max_percentile: null,
                    }
                }
                const response: any = await createUpdateStationMeta(valuesToSend, addAlert);
                if (response?.data.data.updateStationMetadata.message?.length > 0) {
                    setOpen(false);
                    setCreateUpdateStationSuccess(true);
                    errorResponse('', addAlert, `The changes to station, ${response.data.data.updateStationMetadata.data.station_name}, were saved.`, true);
                    setLoading(false)

                }
                if (response.data.errors) {
                    addAlert({
                        id: '',
                        message: response.data.errors[0].message,
                        type: 'error-info',
                        icon: 'fa-exclamation-triangle'
                    })
                    setLoading(false)

                }
            }
            catch (error) {
                console.log('error', error)
                addAlert({
                    id: '',
                    message: alertMessages.retrievingStationData,
                    type: 'error-info',
                    icon: 'fa-exclamation-triangle'
                })
                setLoading(false)
            }
        })();
    }, [addAlert, setOpen,setCreateUpdateStationSuccess, hasHistoric]);

    const handleSubmit = (values: any) => {
        (async () => {
            try {
                setLoading(true)
                updateStationMeta(values)
            }
            catch (error: any) {
                setLoading(false)
                console.log('error', error);
            }
        })();
    };

    /* use Effect to check and make a call to get the edit area */
    useEffect(() => {
        if (editStationId) {
            getMeta(Number(editStationId), stationFailCount)
        }
        else {
            let initValues = {
                stationId: null,
                avg_annual_precip: null,
                vpd_min_percentile: 75,
                vpd_max_percentile: 95,

                weather_daily_visible: true,
                nfdrs_daily_visible: true,
                weather_hourly_visible: true,
                nfdrs_hourly_visible: true,

                weather_daily_download: true,
                weather_hourly_download: true,
                nfdrs_hourly_download: true,
                nfdrs_daily_download: true,

                station_metadata_visible: true,
                produce_nfdrs: true,
                ingest_observation: true,

                nfdrs_visibility: true,
                weather_visibility: true,
            }
            setInitVals(initValues);
        }

    }, [addAlert, editStationId, getMeta, stationFailCount]);

    useEffect(() => {
        if (editStationId && stationFailCount>0 && stationFailCount<4) {
            getMeta(Number(editStationId), stationFailCount)
        }
    },[getMeta, stationFailCount,editStationId ])

    return (
        <GridContainer className="removePadding smooth-scrollbar" style={{ height: '80%', maxHeight: '95%', minHeight: '50%', overflowX: 'hidden', paddingBottom: '2rem' }}>
            {(loading) && (
                <div>
                    <LoadingSpinner />
                </div>
            )}
            <Grid row gap="md" className='page-heading'>
                <Grid
                    desktop={{ col: 12 }}
                    className="display-flex flex-row flex-align-center flex-justify margin-bottom-205 paddingForModal">
                    {
                        editStationId ? (
                            <span className="font-sans-lg text-bold">Edit Station Metadata</span>
                        ) : (
                            <span className="font-sans-lg text-bold">Add a Station</span>
                        )
                    }
                </Grid>
            </Grid>
            <hr style={{ width: '100%', marginBottom: '0', marginTop: '1rem' }}></hr>

            <Formik
                initialValues={initVals}
                validateOnBlur={true}
                validateOnChange={true}
                validateOnMount={true}
                enableReinitialize
                validationSchema={validationSchema}
                onSubmit={(values) => handleSubmit(values)}>
                {({
                    values,
                    errors,
                    touched,
                    handleSubmit,
                    setFieldValue,
                    isValid,
                    setFieldTouched
                }) => {
                    const setValue = (event: any, type: string) => {
                        setFieldTouched(type);
                        if(type.includes('_precip')){
                            if (regex2decimals.test(event.target.value.toString())) {
                                setFieldValue(type, (event.target.value));
                                return
                            }
                        }
                        else if (type.includes('_download')) {
                            setFieldValue(type, event.target.checked);
                        }
                        else if(type.includes('_visible')){
                            setFieldValue(type, event.target.checked);
                            if(type.includes('station_metadata')){
                                setFieldValue('weather_daily_visible',  event.target.checked);
                                setFieldValue('nfdrs_daily_visible',  event.target.checked);
                                setFieldValue('weather_hourly_visible',  event.target.checked);
                                setFieldValue('nfdrs_hourly_visible',  event.target.checked);
                                setFieldValue('weather_daily_download',  event.target.checked);
                                setFieldValue('weather_hourly_download',  event.target.checked);
                                setFieldValue('nfdrs_hourly_download',  event.target.checked);
                                setFieldValue('nfdrs_daily_download',  event.target.checked); 
                                setFieldValue('nfdrs_visibility',  event.target.checked);
                                setFieldValue('weather_visibility',  event.target.checked); 
                            }
                            if(type.includes('nfdrs') && event.target.checked ===true){
                                setFieldValue('nfdrs_visibility',  event.target.checked);
                            }
                            if(type.includes('nfdrs_daily_visible') && event.target.checked===false){
                                if(values.nfdrs_daily_download===false && values.nfdrs_hourly_download===false && values.nfdrs_hourly_visible===false){
                                    setFieldValue('nfdrs_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('nfdrs_daily_download') && event.target.checked===false){
                                if(values.nfdrs_daily_visible===false && values.nfdrs_hourly_download===false && values.nfdrs_hourly_visible===false){
                                    setFieldValue('nfdrs_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('nfdrs_hourly_download') && event.target.checked===false){
                                if(values.nfdrs_daily_visible===false && values.nfdrs_daily_download===false && values.nfdrs_hourly_visible===false){
                                    setFieldValue('nfdrs_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('nfdrs_hourly_visible') && event.target.checked===false){
                                if(values.nfdrs_daily_visible===false && values.nfdrs_daily_download===false && values.nfdrs_hourly_download===false){
                                    setFieldValue('nfdrs_visibility',  event.target.checked);
                                }
                            }

                            if(type.includes('weather') && event.target.checked ===true){
                                setFieldValue('weather_visibility',  event.target.checked);
                            }
                            if(type.includes('weather_daily_visible') && event.target.checked===false){
                                if(values.weather_daily_download===false && values.weather_hourly_download===false && values.weather_hourly_visible===false){
                                    setFieldValue('weather_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('weather_daily_download') && event.target.checked===false){
                                if(values.weather_daily_visible===false && values.weather_hourly_download===false && values.weather_hourly_visible===false){
                                    setFieldValue('weather_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('weather_hourly_download') && event.target.checked===false){
                                if(values.weather_daily_visible===false && values.weather_daily_download===false && values.weather_hourly_visible===false){
                                    setFieldValue('weather_visibility',  event.target.checked);
                                }
                            }
                            if(type.includes('weather_hourly_visible') && event.target.checked===false){
                                if(values.weather_daily_visible===false && values.weather_daily_download===false && values.weather_hourly_download===false){
                                    setFieldValue('weather_visibility',  event.target.checked);
                                }
                            }
                        }
                        else if(type.includes('weather_visibility')){
                            setFieldValue(type, event.target.checked);
                            setFieldValue('weather_daily_visible',  event.target.checked);
                            setFieldValue('weather_hourly_visible',  event.target.checked);
                            setFieldValue('weather_daily_download',  event.target.checked);
                            setFieldValue('weather_daily_download',  event.target.checked);
                            setFieldValue('weather_hourly_download',  event.target.checked);
                        }
                        else if(type.includes('nfdrs_visibility')){
                            setFieldValue(type, event.target.checked);
                            setFieldValue('nfdrs_daily_visible',  event.target.checked);
                            setFieldValue('nfdrs_hourly_visible',  event.target.checked);
                            setFieldValue('nfdrs_daily_download',  event.target.checked);
                            setFieldValue('nfdrs_daily_download',  event.target.checked);
                            setFieldValue('nfdrs_hourly_download',  event.target.checked);
                        }
                        else {                    
                            setFieldValue(type, Number(event.target.value));
                        }
                    }

                    const setIntValue = (event: any, type: string) => {
                        setFieldTouched(type);
                        const inputValue = event.target.value;
                        const filteredValue = inputValue.replaceAll(/[^0-9]/g, '');
                        const intValue = filteredValue === '' ? '' : Number.parseInt(filteredValue, 10);
                        setFieldValue(type, intValue);
                    }  

                    return (
                        <Form onSubmit={handleSubmit}>
                            <div className="paddingForm">
                                <Grid row desktop={{ col: 7 }}>

                                    <label id={'CreateUpdateavg_annual_precip'} htmlFor={'CreateUpdateavg_annual_precip'}>Avg. Annual Precip*</label>
                                    <TextField
                                        value={values.avg_annual_precip}
                                        onBlur={() => setFieldTouched('avg_annual_precip')}
                                        error={!!errors.avg_annual_precip && !!touched.avg_annual_precip}
                                        helperText={errors.avg_annual_precip && touched.avg_annual_precip ? errors.avg_annual_precip : ''}
                                        id={'CreateUpdateavg_annual_precip'}
                                        type={'string'}
                                        size='medium'
                                        fullWidth
                                        onChange={(e: any) => { setValue(e, 'avg_annual_precip') }}
                                        placeholder={'Enter Avg. Annual Precip'}
                                    />
                                </Grid>
                                <Grid row desktop={{ col: 7 }}>

                                    <label id={'CreateUpdate_vpd_min_percentile'} htmlFor={'CreateUpdate_vpd_min_percentile'}>GSI VPD Min*</label>
                                    <TextField
                                        value={values.vpd_min_percentile}
                                        onBlur={() => setFieldTouched('vpd_min_percentile')}
                                        error={!!errors.vpd_min_percentile && !!touched.vpd_min_percentile}
                                        helperText={errors.vpd_min_percentile && touched.vpd_min_percentile ? errors.vpd_min_percentile : ''}
                                        id={'CreateUpdate_vpd_min_percentile'}
                                        type={'string'}
                                        size='medium'
                                        fullWidth
                                        onChange={(e: any) => { setIntValue(e, 'vpd_min_percentile') }}
                                        disabled={!hasHistoric}
                                        placeholder={'Enter VPD Min Percentile'}
                                        onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                                    />
                                    
                                </Grid>
                                <Grid row desktop={{ col: 7 }}>

                                    <label id={'CreateUpdate_vpd_max_percentile'} htmlFor={'CreateUpdate_vpd_max_percentile'}>GSI VPD Max*</label>
                                    <TextField
                                        value={values.vpd_max_percentile}
                                        onBlur={() => setFieldTouched('vpd_max_percentile')}
                                        error={!!errors.vpd_max_percentile && !!touched.vpd_min_percentile}
                                        helperText={errors.vpd_max_percentile && touched.vpd_max_percentile ? errors.vpd_max_percentile : ''}
                                        id={'CreateUpdate_vpd_max_percentile'}
                                        type={'string'}
                                        size='medium'
                                        fullWidth
                                        onInput={(e) => validateInt(e, ValidCharacters.Numeric)}
                                        disabled={!hasHistoric}
                                        onChange={(e: any) => { setIntValue(e, 'vpd_max_percentile') }}
                                        placeholder={'Enter VPD Max Percentile'}
                                    />
                                </Grid>
                            </div>
                            <hr style={{ width: '100%', marginBottom: '1rem', marginTop: '1rem' }}></hr>
                            {/*parent visibilies*/}
                            <div className="paddingForm">

                                <Grid row gap="md">
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Station Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <GreenSwitch
                                                    checked={values.station_metadata_visible}
                                                    onChange={(e) => {
                                                        setValue(e, 'station_metadata_visible');
                                                    }}
                                                    size="medium"
                                                    inputProps={{ 'aria-label': 'Station Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>NFDRS Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.nfdrs_visibility}
                                                    onChange={(e) => {
                                                        setValue(e, 'nfdrs_visibility');
                                                    }}
                                                    size="medium"
                                                    color="secondary"
                                                    inputProps={{ 'aria-label': 'Station Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>

                                </Grid>
                                <Grid row gap="md">
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Weather Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.weather_visibility}
                                                    onChange={(e) => {
                                                        setValue(e, 'weather_visibility');
                                                    }}
                                                    size="medium"
                                                    color="primary"
                                                    inputProps={{ 'aria-label': 'Weather Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>

                                </Grid>
                            </div>

                            <hr style={{ width: '100%', marginBottom: '1rem', marginTop: '1rem' }}></hr>
                            {/*children visibilies*/}
                            <div className="paddingForm">

                                <Grid row gap="md">
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Weather Daily Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.weather_daily_visible}
                                                    onChange={(e) => {
                                                        setValue(e, 'weather_daily_visible');
                                                    }}
                                                    size="medium"
                                                    color="primary"
                                                    inputProps={{ 'aria-label': 'Weather Daily Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>NFDRS Daily Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.nfdrs_daily_visible}
                                                    onChange={(e) => {
                                                        setValue(e, 'nfdrs_daily_visible');
                                                    }}
                                                    size="medium"
                                                    color="secondary"
                                                    inputProps={{ 'aria-label': 'NFDRS Daily Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>

                                </Grid>
                                <Grid row gap="md">
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Weather Hourly Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.weather_hourly_visible}
                                                    onChange={(e) => {
                                                        setValue(e, 'weather_hourly_visible');
                                                    }}
                                                    size="medium"
                                                    color="primary"
                                                    inputProps={{ 'aria-label': 'Weather Hourly Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>NFDRS Hourly Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.nfdrs_hourly_visible}
                                                    onChange={(e) => {
                                                        setValue(e, 'nfdrs_hourly_visible');
                                                    }}
                                                    size="medium"
                                                    color="secondary"
                                                    inputProps={{ 'aria-label': 'NFDRS Hourly Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Weather Daily Download Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.weather_daily_download}
                                                    onChange={(e) => {
                                                        setValue(e, 'weather_daily_download');
                                                    }}
                                                    size="medium"
                                                    color="primary"
                                                    inputProps={{ 'aria-label': 'Weather Daily Download Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>NFDRS Daily Download Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.nfdrs_daily_download}
                                                    onChange={(e) => {
                                                        setValue(e, 'nfdrs_daily_download');
                                                    }}
                                                    size="medium"
                                                    color="secondary"
                                                    inputProps={{ 'aria-label': 'NFDRS Daily Download Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>Weather Hourly Download Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.weather_hourly_download}
                                                    onChange={(e) => {
                                                        setValue(e, 'weather_hourly_download');
                                                    }}
                                                    size="medium"
                                                    color="primary"
                                                    inputProps={{ 'aria-label': 'Weather Hourly Download Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                    <Grid desktop={{col:6}}>
                                        <Typography style={{fontSize: '18px', fontWeight: 700}}>NFDRS Hourly Download Visibility</Typography>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={values.nfdrs_hourly_download}
                                                    onChange={(e) => {
                                                        setValue(e, 'nfdrs_hourly_download');
                                                    }}
                                                    size="medium"
                                                    color="secondary"
                                                    inputProps={{ 'aria-label': 'NFDRS Hourly Download Visibility toggle' }}
                                                />
                                            }
                                            label="Visible"
                                        />
                                    </Grid>
                                </Grid>
                            </div>


                            <Grid row className='modalBottomDiv'>
                                <Grid desktop={{ col: 10 }} className="modalCancelDiv">
                                    <Button variant="outlined" style={{ backgroundColor: 'white' }} onClick={() => { setOpen(false) }}>
                                        Cancel
                                    </Button>
                                </Grid>
                                {
                                    editStationId ? (
                                        <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                                            <Button variant="contained" type="submit" aria-disabled={!isValid}>
                                                Save
                                            </Button>
                                        </Grid>
                                    ) : (
                                        <Grid desktop={{ col: 2 }} className='modalSubmitDiv'>
                                            <Button variant="contained" type="submit" aria-disabled={!isValid}>
                                                Add
                                            </Button>
                                        </Grid>
                                    )
                                }
                            </Grid>
                        </Form>
                    )
                }}
            </Formik>
        </GridContainer>
    )
}